import axiosClient, { axiosClientWithoutToken } from './config';
import { sanitizeCartographyShareFilters } from '../utils/cartographyShareFilters';

export type CartographyShareFilters = {
  catalog_key?: string;
  badge_series_id?: number;
  level?: string;
  searchTerm?: string;
  student_id?: string;
  student_ids?: string[];
};

/**
 * Create a shareable link for badge cartography
 */
export const createBadgeCartographyShare = async (
  filters: CartographyShareFilters,
  context: {
    showingPageType: 'user' | 'pro' | 'edu' | 'teacher' | 'of';
    organizationId?: number;
    organizationName?: string;
  }
): Promise<{ shareable_url: string; token: string; expires_at: string }> => {
  const identity = sanitizeCartographyShareFilters(filters);
  const response = await axiosClient.post('/api/v1/badges/cartography/share', {
    filters: {
      ...identity,
      student_id: filters.student_id,
      student_ids: filters.student_ids,
    },
    context
  });
  return response.data;
};

/**
 * Get public badge cartography data by token
 * Uses axiosClientWithoutToken since this is a public endpoint
 */
export const getPublicBadgeCartography = async (token: string): Promise<any> => {
  const response = await axiosClientWithoutToken.get(`/badge-cartography/${token}`);
  return response.data;
};

/**
 * Get selected students badge cartography data by token
 * Uses axiosClientWithoutToken since this is a public endpoint
 */
export const getSelectedStudentsBadgeCartography = async (token: string): Promise<any> => {
  const response = await axiosClientWithoutToken.get(`/badge-cartography-selected/${token}`);
  return response.data;
};

/**
 * Create a shareable link for a specific student's badge cartography
 */
export const createStudentBadgeCartographyShare = async (
  schoolId: number,
  studentId: string | number
): Promise<{ shareable_url: string; token: string; expires_at: string }> => {
  const response = await axiosClient.post(`/api/v1/schools/${schoolId}/badges/cartography/share`, {
    filters: {
      student_id: studentId.toString()
    }
  });
  return response.data;
};

/**
 * Create a shareable link for a specific student's badge cartography (teacher endpoint)
 */
export const createTeacherStudentBadgeCartographyShare = async (
  studentId: string | number,
  schoolId?: number | null
): Promise<{ shareable_url: string; token: string; expires_at: string }> => {
  const params: any = {
    filters: {
      student_id: studentId.toString()
    }
  };
  
  if (schoolId) {
    params.school_id = schoolId;
  }
  
  const response = await axiosClient.post('/api/v1/teachers/badges/cartography/share', params);
  return response.data;
};

/**
 * Create a shareable link for multiple selected students' badge cartography
 */
export const createSelectedStudentsBadgeCartographyShare = async (
  schoolId: number,
  studentIds: (string | number)[]
): Promise<{ shareable_url: string; token: string; expires_at: string }> => {
  const response = await axiosClient.post(`/api/v1/schools/${schoolId}/badges/cartography/share`, {
    filters: {
      student_ids: studentIds.map(id => id.toString())
    }
  });
  return response.data;
};

/**
 * Revoke a shareable link
 */
export const revokeBadgeCartographyShare = async (shareId: number): Promise<void> => {
  await axiosClient.delete(`/api/v1/badges/cartography/shares/${shareId}`);
};
